import {useCallback, useEffect, useState} from 'react';
import {diffJson} from 'diff';
import stringify from 'json-stable-stringify';
import pickBy from 'lodash/pickBy';

import {useConfig} from '@/contexts/config';
import {DocumentData, Model, ValueResolver, VisibilitySet} from '@/engine';
import {FullDocument} from '@/interfaces/document';
import {serializeDocument} from '@/utils/document/serializer';
import {useDocumentRepository} from '../repository';
import {useDocumentUtils} from '../utils';

type Props = {
  data: DocumentData;
  visibilitySet: Set<string>;
};

export function useDocumentHasUnsavedChanges({data, visibilitySet}: Props) {
  const {model} = useConfig();
  const {document} = useDocumentRepository();
  const {valueResolver} = useDocumentUtils();
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const computeHasChanged = useCallback(
    (data: DocumentData) => computeDocumentDataHasChanged(document, data, model, visibilitySet, valueResolver),
    [document, model, visibilitySet, valueResolver],
  );

  useEffect(() => {
    const hasChanged = computeHasChanged(data);
    setHasUnsavedChanges(hasChanged);
  }, [computeHasChanged, data]);

  return hasUnsavedChanges;
}

const hasDifferences = (remote: unknown, local: unknown) => {
  const diff = diffJson(stringify(remote) ?? '', stringify(local) ?? '');
  const hasDiff = diff.length > 1 || diff[0].added || diff[0].removed;
  return hasDiff;
};

const filterImmutableFields = (_: unknown, key: string) => {
  return key.startsWith('section') && !['sectionCONS', 'sectionINVE', 'sectionUSER'].includes(key);
};

const computeDocumentDataHasChanged = (
  document: FullDocument,
  data: DocumentData,
  model: Model,
  visibilitySet: VisibilitySet,
  valueResolver: ValueResolver,
) => {
  const serializedDocument = serializeDocument(data, model, visibilitySet, valueResolver);
  const strippedSerializedDocument = pickBy(serializedDocument, filterImmutableFields);
  const strippedDocument = pickBy(document, filterImmutableFields);
  const hasDiff = hasDifferences(strippedDocument, strippedSerializedDocument);
  return hasDiff;
};
